import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { api, formatApiErrorDetail, formatCurrencyEUR, formatDate } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import PortalLayout from "../components/PortalLayout";
import ComplianceDisclaimers from "../components/ComplianceDisclaimers";
import AssetAllocation from "../components/AssetAllocation";

const DURATION_ORDER = { "12": 1, "24": 2, "36": 3, open: 4 };

export default function PlansPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const isIntroducer = user && user.role === "introducer";
  const [kycComplete, setKycComplete] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pendingIds, setPendingIds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Confirmation modal state
  const [modalPlan, setModalPlan] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [kidAck, setKidAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      // Introducers see the plans as a showcase — they never subscribe, so
      // don't hit the personal /investment-requests/me endpoint.
      const promises = [api.get("/plans/available")];
      if (!isIntroducer) {
        promises.push(api.get("/investment-requests/me"));
      }
      const [avail, mine] = await Promise.all(promises);
      setKycComplete(Boolean(avail.data.kyc_complete));
      setPlans(avail.data.plans || []);
      setPendingIds(avail.data.pending_plan_ids || []);
      setRequests(isIntroducer ? [] : (mine?.data || []));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("plans.toast_err"));
    } finally {
      setLoading(false);
    }
  }, [t, isIntroducer]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of plans) {
      if (!map.has(p.tier)) map.set(p.tier, []);
      map.get(p.tier).push(p);
    }
    for (const arr of map.values()) {
      arr.sort(
        (a, b) => (DURATION_ORDER[a.duration] || 9) - (DURATION_ORDER[b.duration] || 9),
      );
    }
    return Array.from(map.values());
  }, [plans]);

  const openModal = (plan) => {
    setError("");
    setSuccess("");
    setAmount(plan.min_amount ? String(plan.min_amount) : "");
    setNote("");
    setKidAck(false);
    setModalPlan(plan);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalPlan(null);
  };

  const submit = async () => {
    if (!modalPlan) return;
    setSubmitting(true);
    setError("");
    try {
      const body = { plan_id: modalPlan.id, kid_ack: kidAck };
      if (amount && Number(amount) > 0) body.amount = Number(amount);
      if (note.trim()) body.note = note.trim();
      const resp = await api.post("/investment-requests", body);
      setModalPlan(null);
      setSuccess(resp.data?.status === "awaiting_kyc" ? t("plans.toast_ok_kyc") : t("plans.toast_ok"));
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("plans.toast_err"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-[1180px] mx-auto px-6 py-14" data-testid="plans-page">
        <div className="br-fade-up">
          <div className="br-overline">{t("plans.overline")}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 text-[color:var(--br-parchment)]">
            {t("plans.title")}
          </h1>
          <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-4 leading-relaxed max-w-[680px]">
            {t("plans.intro")}
          </p>
        </div>

        {success ? (
          <div
            className="mt-8 br-card px-6 py-4 text-[0.9rem]"
            style={{ borderLeft: "2px solid #5f8a6b", color: "var(--br-parchment)" }}
            data-testid="plans-success"
          >
            {success}
          </div>
        ) : null}

        {error && !modalPlan ? (
          <div
            className="mt-8 br-card px-6 py-4 text-[0.9rem]"
            style={{ borderLeft: "2px solid #b36b6b", color: "var(--br-parchment)" }}
            data-testid="plans-error"
          >
            {error}
          </div>
        ) : null}

        {!loading && isIntroducer ? (
          <div
            className="mt-8 br-card px-8 py-7"
            style={{ borderLeft: "2px solid var(--br-bronze-soft)" }}
            data-testid="plans-partner-banner"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[280px]">
                <div
                  className="font-mono-fin text-[0.62rem] tracking-[0.24em] uppercase"
                  style={{ color: "var(--br-bronze-soft)" }}
                >
                  {t("plans.partner_title")}
                </div>
                <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed max-w-[680px]">
                  {t("plans.partner_body")}
                </p>
              </div>
              <IntroducerShareCard />
            </div>
          </div>
        ) : !loading && !kycComplete ? (
          <div
            className="mt-8 br-card px-8 py-7"
            style={{ borderLeft: "2px solid var(--br-bronze)" }}
            data-testid="plans-kyc-soft-banner"
          >
            <div
              className="font-mono-fin text-[0.62rem] tracking-[0.24em] uppercase"
              style={{ color: "var(--br-bronze)" }}
            >
              {t("plans.kyc_title")}
            </div>
            <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed max-w-[560px]">
              {t("plans.kyc_body")}
            </p>
            <Link
              to="/kyc"
              className="br-btn-outline inline-block mt-5"
              data-testid="plans-kyc-cta"
            >
              {t("plans.kyc_cta")}
            </Link>
          </div>
        ) : null}

        {loading ? (
          <div
            className="mt-12 text-[0.9rem] text-[color:var(--br-text-dim)]"
            data-testid="plans-loading"
          >
            {t("plans.loading")}
          </div>
        ) : (
          <div className="mt-12 space-y-14">
            {groups.map((group, gi) => {
              const tierName = (group[0]?.name || "").split("·")[0].trim();
              return (
                <section key={gi} data-testid={`plan-tier-${group[0]?.tier || gi}`}>
                  <h2 className="font-serif-display text-2xl text-[color:var(--br-parchment)] mb-6">
                    {tierName}
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                    {group.map((p) => (
                      <PlanCard
                        key={p.id}
                        plan={p}
                        locale={locale}
                        kycComplete={kycComplete}
                        pending={pendingIds.includes(p.id)}
                        onRequest={() => openModal(p)}
                        t={t}
                        isIntroducer={isIntroducer}
                      />
                    ))}
                  </div>
                  {group[0]?.asset_allocation?.length ? (
                    <div className="br-card px-6 py-6 mt-6">
                      <AssetAllocation
                        allocation={group[0].asset_allocation}
                        volatilityTarget={group[0].volatility_target}
                        collapsible
                      />
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}

        {/* My requests */}
        {!loading ? (
          <section className="mt-16" data-testid="plans-my-requests">
            <h2 className="font-serif-display text-2xl text-[color:var(--br-parchment)] mb-6">
              {t("plans.my_requests")}
            </h2>
            {requests.length === 0 ? (
              <p className="text-[0.9rem] text-[color:var(--br-text-dim)]">
                {t("plans.no_requests")}
              </p>
            ) : (
              <div className="space-y-3">
                {requests.map((r) => (
                  <div
                    key={r.id}
                    className="br-card px-6 py-4 flex flex-wrap items-center gap-4 justify-between"
                    data-testid={`request-row-${r.id}`}
                  >
                    <div>
                      <div className="text-[1rem] text-[color:var(--br-parchment)]">
                        {r.plan?.name || "—"}
                      </div>
                      <div className="text-[0.78rem] text-[color:var(--br-text-dim)] mt-1">
                        {r.amount ? formatCurrencyEUR(r.amount, locale) : "—"} ·{" "}
                        {formatDate(r.created_at, locale)}
                      </div>
                      {r.decision_comment ? (
                        <div className="text-[0.8rem] text-[color:var(--br-text-dim)] mt-2 italic max-w-[560px]">
                          “{r.decision_comment}”
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {r.status === "awaiting_kyc" ? (
                        <Link
                          to="/kyc"
                          className="br-btn-outline"
                          style={{ padding: "8px 14px", fontSize: "0.68rem" }}
                          data-testid={`request-kyc-cta-${r.id}`}
                        >
                          {t("plans.kyc_cta")}
                        </Link>
                      ) : null}
                      <StatusBadge status={r.status} t={t} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        <div className="mt-14">
          <ComplianceDisclaimers />
        </div>
      </div>

      {modalPlan ? (
        <ConfirmModal
          plan={modalPlan}
          locale={locale}
          amount={amount}
          setAmount={setAmount}
          note={note}
          setNote={setNote}
          kidAck={kidAck}
          setKidAck={setKidAck}
          submitting={submitting}
          error={error}
          onCancel={closeModal}
          onConfirm={submit}
          t={t}
        />
      ) : null}
    </PortalLayout>
  );
}

function PlanCard({ plan, locale, pending, onRequest, t, isIntroducer }) {
  const lock = plan.lock_period_months
    ? `${plan.lock_period_months} ${t("calc.months")}`
    : t("calc.lock_open");
  return (
    <div
      className="br-card px-6 py-6 flex flex-col"
      data-testid={`plan-card-${plan.id}`}
    >
      <div
        className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase"
        style={{ color: "var(--br-bronze)" }}
      >
        {t(`calc.duration_${plan.duration}`)}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span
          className="font-serif-display text-[2.4rem] leading-none"
          style={{ color: "var(--br-bronze)" }}
        >
          {plan.gross_rate.toFixed(2)}
        </span>
        <span className="text-[0.9rem] text-[color:var(--br-text-dim)]">%</span>
      </div>
      <div className="text-[0.68rem] text-[color:var(--br-text-dim)] mt-1">
        {t("calc.rate_gross")}
      </div>

      <div className="mt-5 space-y-2 text-[0.8rem]">
        <Row label={t("plans.range")} value={`${formatCurrencyEUR(plan.min_amount, locale)} – ${formatCurrencyEUR(plan.max_amount, locale)}`} />
        <Row label={t("calc.mngmt_fee")} value={`${plan.mngmt_fee.toFixed(2)} %`} />
        <Row label={t("calc.perf_fee")} value={`${plan.perf_fee.toFixed(2)} %`} />
        <Row label={t("calc.lock_period")} value={lock} />
      </div>

      <a
        href={`/api/plans/${plan.id}/kid?lang=${locale}`}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-2 font-mono-fin text-[0.62rem] tracking-[0.16em] uppercase"
        style={{ color: "var(--br-bronze)" }}
        data-testid={`plan-kid-link-${plan.id}`}
      >
        <FileText size={13} />
        {t("plans.kid_download")}
      </a>

      <div className="mt-6 pt-5" style={{ borderTop: "1px solid var(--br-line)" }}>
        {isIntroducer ? (
          <div
            className="text-center font-mono-fin text-[0.62rem] tracking-[0.2em] uppercase py-2.5"
            style={{ color: "var(--br-bronze-soft)" }}
            data-testid={`plan-partner-mode-${plan.id}`}
          >
            {t("plans.partner_showcase_badge")}
          </div>
        ) : pending ? (
          <div
            className="text-center font-mono-fin text-[0.62rem] tracking-[0.2em] uppercase py-2.5"
            style={{ color: "var(--br-bronze)" }}
            data-testid={`plan-pending-${plan.id}`}
          >
            {t("plans.pending_badge")}
          </div>
        ) : (
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 accent-[color:var(--br-bronze)] h-4 w-4"
              checked={false}
              onChange={() => onRequest()}
              data-testid={`plan-checkbox-${plan.id}`}
            />
            <span className="text-[0.78rem] leading-snug text-[color:var(--br-text-dim)]">
              {t("plans.checkbox")}
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[0.62rem] tracking-[0.14em] uppercase text-[color:var(--br-text-dim)] font-mono-fin">
        {label}
      </span>
      <span className="text-[color:var(--br-parchment)]">{value}</span>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    awaiting_kyc: { label: t("plans.status_awaiting_kyc"), color: "#8a97b8" },
    pending: { label: t("plans.status_pending"), color: "var(--br-bronze)" },
    approved: { label: t("plans.status_approved"), color: "#5f8a6b" },
    rejected: { label: t("plans.status_rejected"), color: "#b36b6b" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
      style={{ color: s.color, border: `1px solid ${s.color}` }}
      data-testid={`request-status-${status}`}
    >
      {s.label}
    </span>
  );
}

function ConfirmModal({
  plan,
  locale,
  amount,
  setAmount,
  note,
  setNote,
  kidAck,
  setKidAck,
  submitting,
  error,
  onCancel,
  onConfirm,
  t,
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(8,10,12,0.82)", backdropFilter: "blur(4px)" }}
      data-testid="plan-confirm-modal"
    >
      <div
        className="br-card w-full max-w-[560px] max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--br-bg-inner, #12151a)" }}
      >
        <div className="px-8 py-6 border-b" style={{ borderColor: "var(--br-line)" }}>
          <div className="br-overline">{plan.name}</div>
          <h3 className="font-serif-display text-2xl mt-2 text-[color:var(--br-parchment)]">
            {t("plans.modal_title")}
          </h3>
        </div>
        <div className="px-8 py-6">
          <p className="text-[0.9rem] leading-relaxed text-[color:var(--br-text-dim)]">
            {t("plans.modal_body", { plan: plan.name })}
          </p>

          <div className="mt-6">
            <label className="block text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)] font-mono-fin mb-2">
              {t("plans.amount_label")}
            </label>
            <input
              type="number"
              min={plan.min_amount}
              max={plan.max_amount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-transparent border rounded px-4 py-2.5 text-[color:var(--br-parchment)] outline-none"
              style={{ borderColor: "var(--br-line)" }}
              data-testid="plan-amount-input"
            />
            <p className="text-[0.72rem] text-[color:var(--br-text-dim)] mt-2">
              {t("plans.amount_help")} · {formatCurrencyEUR(plan.min_amount, locale)} –{" "}
              {formatCurrencyEUR(plan.max_amount, locale)}
            </p>
          </div>

          <div className="mt-5">
            <label className="block text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)] font-mono-fin mb-2">
              {t("plans.note_label")}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={800}
              className="w-full bg-transparent border rounded px-4 py-2.5 text-[color:var(--br-parchment)] outline-none resize-none"
              style={{ borderColor: "var(--br-line)" }}
              data-testid="plan-note-input"
            />
          </div>

          <div
            className="mt-6 px-5 py-4 border rounded"
            style={{ borderColor: "var(--br-line)", background: "rgba(167,128,73,0.05)" }}
            data-testid="plan-kid-block"
          >
            <p className="text-[0.78rem] text-[color:var(--br-text-dim)] leading-relaxed">
              {t("plans.kid_modal_hint")}
            </p>
            <a
              href={`/api/plans/${plan.id}/kid?lang=${locale}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 font-mono-fin text-[0.62rem] tracking-[0.16em] uppercase"
              style={{ color: "var(--br-bronze)" }}
              data-testid="plan-modal-kid-link"
            >
              <FileText size={13} />
              {t("plans.kid_download")}
            </a>
            <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={kidAck}
                onChange={(e) => setKidAck(e.target.checked)}
                className="mt-0.5 accent-[color:var(--br-bronze)] h-4 w-4"
                data-testid="plan-kid-ack-checkbox"
              />
              <span className="text-[0.8rem] leading-snug text-[color:var(--br-parchment)]">
                {t("plans.kid_ack_label")}
              </span>
            </label>
          </div>

          {error ? (
            <div
              className="mt-4 text-[0.85rem]"
              style={{ color: "#d98a8a" }}
              data-testid="plan-modal-error"
            >
              {error}
            </div>
          ) : null}
        </div>
        <div
          className="px-8 py-5 flex items-center justify-end gap-3 border-t"
          style={{ borderColor: "var(--br-line)" }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="br-btn-outline"
            data-testid="plan-modal-cancel"
          >
            {t("plans.modal_cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || !kidAck}
            className="br-btn-primary"
            style={!kidAck ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
            data-testid="plan-modal-confirm"
          >
            {submitting ? "…" : t("plans.modal_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Compact "share your code" card shown on the Plans page while an introducer
 * is in Partner mode. Fetches once, caches. Renders code + copy button. This
 * lets the introducer share the link with prospects DURING the pitch itself.
 */
function IntroducerShareCard() {
  const { t } = useI18n();
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/introducer/me")
      .then(({ data }) => setMe(data))
      .catch((err) => {
        // The share card is optional — don't disrupt the page if the endpoint
        // fails (e.g. when a non-introducer inadvertently mounts this).
        console.debug("[plans] introducer share card fetch failed:", err?.message || err);
      });
  }, []);

  if (!me) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(me.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.warn("[plans] copy link failed:", err?.message || err);
    }
  };

  return (
    <div
      className="border px-5 py-4 min-w-[260px]"
      style={{ borderColor: "var(--br-bronze)", background: "rgba(196,155,102,0.06)" }}
      data-testid="plans-partner-share-card"
    >
      <div
        className="font-mono-fin text-[0.55rem] tracking-[0.22em] uppercase"
        style={{ color: "var(--br-bronze)" }}
      >
        {t("plans.partner_your_code")}
      </div>
      <div
        className="font-mono-fin text-[0.95rem] mt-2"
        style={{ color: "var(--br-parchment)" }}
        data-testid="plans-partner-code"
      >
        {me.code}
      </div>
      <button
        type="button"
        onClick={copyLink}
        className="mt-3 inline-flex items-center gap-2 font-mono-fin text-[0.62rem] tracking-[0.18em] uppercase transition-colors hover:text-[color:var(--br-parchment)]"
        style={{ color: "var(--br-bronze-soft)" }}
        data-testid="plans-partner-copy-link"
      >
        {copied ? "✓ " + t("intro.copied") : t("plans.partner_copy_link")}
      </button>
    </div>
  );
}

